import { Injectable } from '@angular/core';
import { ApiService, GenericResponse } from '@uranda/commons';
import { HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Quote } from '../../models/quote.model';

@Injectable({
    providedIn: 'root'
})
export class RequestService extends ApiService {

    async verifyRequest(token: string): Promise<GenericResponse<{ token: string }>> {
        let queryParameters = new HttpParams()
            .set('token', token)
      const response = await lastValueFrom(this.httpClient.get<any>(
        `${this.getApiUrl()}/v1/request/verify`,
        { params: queryParameters, withCredentials: true }
      ));
      return response.data;
    }

    async getRequest(token: string, language?: string): Promise<RequestResponse> {
        let params = new HttpParams().set('token', token);

        if (language && language !== 'wl') {
            params = params.set('lang', language);
        }

        try {
            const response = await lastValueFrom(this.httpClient.get<any>(`${this.getApiUrl()}/v1/requests`, {
                params,
                withCredentials: true
            }))

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    private async getQuotes(endpoint: string, token: string, language?: string, sort?: string): Promise<Quote[]> {
        let params = new HttpParams().set('token', token);
        console.log('Getting quotes with params:', language, sort);
        if (language && language !== 'wl') {
            params = params.set('languagesSpoken', language);
        }

        if (sort) {
            const [orderBy, direction] = sort.split('-');
            params = params.set('orderBy', orderBy);
            if (direction) {
                params = params.set('direction', direction);
            }
        }

        try {
            const response = await lastValueFrom(this.httpClient.get<any>(`${this.getApiUrl()}${endpoint}`, {
                params,
                withCredentials: true
            }))

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getNewQuotes(token: string, language?: string, sort?: string): Promise<Quote[]> {
        return this.getQuotes('/v1/requests/quotes/new', token, language, sort);
    }

    async getReceivedQuotes(token: string, language?: string, sort?: string): Promise<Quote[]> {
        return this.getQuotes('/v1/requests/quotes/received', token, language, sort);
    }

    async getWaitingQuotes(token: string, language?: string, sort?: string): Promise<Quote[]> {
        return this.getQuotes('/v1/requests/quotes/waiting', token, language, sort);
    }


    async quoteInterest(token: string, quoteId: number) {
        let queryParameters = new HttpParams()
            .set('token', token)
            .set('quoteId', quoteId);

        try {
            const response = await lastValueFrom(this.httpClient.post<any>(`${this.getApiUrl()}/v1/requests/quote/interest`, queryParameters, {
                withCredentials: true
            }))

            return response.data;
        } catch (error) {
            throw error;
        }
    }


    async verifyIdentity(token: string, otp?: string) {
        let queryParameters = new HttpParams()
            .set('token', token)
            .set('otp', otp ?? '');
        const response = await lastValueFrom(this.httpClient.get<any>(
            `${this.getApiUrl()}/v1/requests/verify-identity`,
            { params: queryParameters, withCredentials: true }
        ));
        return response.data;
    }


    async resendOtp(token: string) {
        let queryParameters = new HttpParams()
            .set('token', token);
        const response = await this.get("/v1/requests/resend-otp", queryParameters);
        return response.data;
    }

    async enableQuotes(token: string, requestId: number): Promise<void> {
        const params = new HttpParams()
            .set('token', token);

        try {
            await lastValueFrom(this.httpClient.put<any>(
                `${this.getApiUrl()}/v1/requests/enable`,
                { requestId },
                { params, withCredentials: true }
            ));
        } catch (error) {
            throw error;
        }
    }

    async disableQuotes(token: string, requestId: number): Promise<void> {
        const params = new HttpParams()
            .set('token', token);

        try {
            await lastValueFrom(this.httpClient.put<any>(
                `${this.getApiUrl()}/v1/requests/disable`,
                { requestId },
                { params, withCredentials: true }
            ));
        } catch (error) {
            throw error;
        }
    }

    async cancelQuoteRequest(token: string): Promise<boolean> {
        const params = new HttpParams()
            .set('token', token);

        try {
            const response = await lastValueFrom(this.httpClient.delete<any>(
                `${this.getApiUrl()}/v1/requests/quote/cancel`,
                { params, withCredentials: true }
            ));
            return response.data;
        } catch (error) {
            throw error;
        }
    }

}

export interface RequestResponse {
    request: any;
}

export interface CustomerAreaResponse {
    request: any;
    quotes: {
        newQuotes: Quote[];
        receivedQuotes: Quote[];
        waitingForQuote: Quote[];
    };
}
